import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import { strCode } from "../../../../../../../consts";
import { simpleListSections } from "../../../../consts/simpleListSections";
import { t } from "../../../../../../../services";
import { useDebounce } from "../../../../../../../hooks";

import {
  FlexContainers,
  PageSelector,
  TextArea,
  WrapperContainer
} from "../../../../../../../components";
import { SimpleListWrapper } from "./styled";

const sections = [
  { id: simpleListSections.newFeaturesList, title: strCode.NEW_FEATURES_TITLE },
  { id: simpleListSections.improvementsList, title: strCode.IMPROVEMENTS_TITLE }
];

export const SimpleList = ({
  language,
  config,
  configErrors,
  showErrors,
  languageListLength,
  onChange,
  pushErrorLang,
  removeErrorLang
}) => {
  const [activePage, setActivePage] = useState(simpleListSections.newFeaturesList);

  const [newFeaturesList, setNewFeaturesList] = useState("");
  const [improvementsList, setImprovementsList] = useState("");

  const errorPages = useMemo(() => {
    const errors = [];

    if (configErrors?.newFeaturesList) {
      errors.push(simpleListSections.newFeaturesList);
    }

    if (configErrors?.improvementsList) {
      errors.push(simpleListSections.improvementsList);
    }

    return errors;
  }, [configErrors]);

  useEffect(() => {
    setNewFeaturesList(config?.newFeaturesList ?? "");
    setImprovementsList(config?.improvementsList ?? "");

    setActivePage(simpleListSections.newFeaturesList);
  }, [config]);

  useEffect(() => {
    return () => {
      pushErrorLang(language);
    };
  }, [language]);

  useEffect(() => {
    if (!errorPages?.length && !configErrors?.summary) {
      removeErrorLang(language);
    }
  }, [language, errorPages]);

  const saveConfig = () => {
    const newConfig = { newFeaturesList, improvementsList };
    onChange(newConfig);
  };

  useDebounce(saveConfig, 100, newFeaturesList, improvementsList, languageListLength);

  return (
    <SimpleListWrapper Container={FlexContainers.FlexColumnStart} gap={16} width={"100%"}>
      <PageSelector
        pages={sections}
        activePage={activePage}
        errorPages={showErrors ? errorPages : []}
        setActivePage={setActivePage}
      />

      <WrapperContainer mt={16} ph={16} width={"100%"}>
        {activePage === simpleListSections.newFeaturesList ? (
          <TextArea
            id={"new-features-list"}
            title={t(strCode.BODY_TITLE)}
            value={newFeaturesList}
            setValue={setNewFeaturesList}
            placeholder={t(strCode.BULLET_LIST_PLACEHOLDER)}
            hasError={showErrors && configErrors?.newFeaturesList}
            isBulletList
          />
        ) : (
          <TextArea
            id={"improvements-list"}
            title={t(strCode.BODY_TITLE)}
            value={improvementsList}
            setValue={setImprovementsList}
            placeholder={t(strCode.BULLET_LIST_PLACEHOLDER)}
            hasError={showErrors && configErrors?.improvementsList}
            isBulletList
          />
        )}
      </WrapperContainer>
    </SimpleListWrapper>
  );
};

SimpleList.propTypes = {
  language: PropTypes.string.isRequired,
  config: {
    newFeaturesList: PropTypes.string.isRequired,
    improvementsList: PropTypes.string.isRequired
  },
  configErrors: PropTypes.shape({
    language: PropTypes.string.isRequired,
    summary: PropTypes.bool,
    newFeaturesList: PropTypes.string.isRequired,
    improvementsList: PropTypes.string.isRequired
  }),
  showErrors: PropTypes.bool.isRequired,
  languageListLength: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  pushErrorLang: PropTypes.func.isRequired,
  removeErrorLang: PropTypes.func.isRequired
};
